import { DarkTheme as NavDarkTheme, DefaultTheme as NavDefaultTheme } from "@react-navigation/native";
import { useColorScheme } from "react-native";
import { MD3DarkTheme, MD3LightTheme } from "react-native-paper";

import { useAppStore } from "../stores/useStore";

/**
 * アプリ全体で共有するデザイントークン。
 * ライト／ダークで同じキー構成のパレットを持ち、useColors() で
 * 現在のモードに応じたパレットを取得する。色以外（余白・角丸）はモード非依存。
 */
export interface ThemeColors {
  primary: string;
  primaryDark: string;
  primarySoft: string;
  accent: string;
  success: string;
  warning: string;
  danger: string;

  bg: string;
  surface: string;
  surfaceAlt: string;
  border: string;

  textPrimary: string;
  textSecondary: string;
  textMuted: string;

  overlay: string;
}

export const lightColors: ThemeColors = {
  primary: "#3182ce",
  primaryDark: "#1f6fb2",
  primarySoft: "#e7f5ff",
  accent: "#805ad5",
  success: "#38a169",
  warning: "#d69e2e",
  danger: "#e53e3e",

  bg: "#f6f8fb",
  surface: "#ffffff",
  surfaceAlt: "#f1f4f9",
  border: "#e7ebf0",

  textPrimary: "#1a202c",
  textSecondary: "#5f6b7a",
  textMuted: "#97a1ad",

  overlay: "rgba(15, 31, 51, 0.55)",
};

export const darkColors: ThemeColors = {
  primary: "#4aa3e8",
  primaryDark: "#8cc6f2",
  primarySoft: "#15324d",
  accent: "#b794f4",
  success: "#68d391",
  warning: "#f6c149",
  danger: "#fc8181",

  bg: "#0f141a",
  surface: "#1a212b",
  surfaceAlt: "#232c38",
  border: "#2d3744",

  textPrimary: "#f1f4f8",
  textSecondary: "#aab4c0",
  textMuted: "#7a8696",

  overlay: "rgba(0, 0, 0, 0.65)",
};

/** 後方互換：トークン未対応箇所のためにライトを既定 export */
export const colors = lightColors;

export type ThemeMode = "system" | "light" | "dark";

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
} as const;

export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  pill: 999,
} as const;

export const shadow = {
  card: {
    shadowColor: "#0b1f33",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
} as const;

/** 現在の有効なカラースキーム（"system" はOS設定に従う）を返す */
export const useResolvedScheme = (): "light" | "dark" => {
  const mode = useAppStore((state) => state.themeMode);
  const systemScheme = useColorScheme();
  if (mode === "system") return systemScheme === "dark" ? "dark" : "light";
  return mode;
};

/** 現在のモードに対応したカラーパレットを返す */
export const useColors = (): ThemeColors =>
  useResolvedScheme() === "dark" ? darkColors : lightColors;

const buildPaperTheme = (scheme: "light" | "dark") => {
  const base = scheme === "dark" ? MD3DarkTheme : MD3LightTheme;
  const c = scheme === "dark" ? darkColors : lightColors;
  return {
    ...base,
    roundness: radius.md,
    colors: {
      ...base.colors,
      primary: c.primary,
      secondary: c.accent,
      background: c.bg,
      surface: c.surface,
      surfaceVariant: c.surfaceAlt,
      error: c.danger,
      outline: c.border,
      onSurface: c.textPrimary,
      onSurfaceVariant: c.textSecondary,
      elevation: {
        ...base.colors.elevation,
        level0: "transparent",
        level1: c.surface,
        level2: c.surface,
        level3: c.surface,
        level4: c.surface,
        level5: c.surface,
      },
    },
  };
};

export const paperLightTheme = buildPaperTheme("light");
export const paperDarkTheme = buildPaperTheme("dark");

export const navigationLightTheme = {
  ...NavDefaultTheme,
  colors: {
    ...NavDefaultTheme.colors,
    primary: lightColors.primary,
    background: lightColors.bg,
    card: lightColors.surface,
    border: lightColors.border,
    text: lightColors.textPrimary,
  },
};

export const navigationDarkTheme = {
  ...NavDarkTheme,
  colors: {
    ...NavDarkTheme.colors,
    primary: darkColors.primary,
    background: darkColors.bg,
    card: darkColors.surface,
    border: darkColors.border,
    text: darkColors.textPrimary,
  },
};

/** 後方互換 export（ライト） */
export const paperTheme = paperLightTheme;
export const navigationTheme = navigationLightTheme;
